import { LicensePlate } from '@navio/nsdk';
import { NoddiIcon, NoddiIconName } from 'noddi-ui-common';
import { formatLicensePlate, pluralize } from 'noddi-util';
import { ReactNode } from 'react';

type Props = {
  salesItems: {
    id: number;
    name: string;
    priceLabel?: ReactNode;
    quantity?: number;
    icon?: NoddiIconName;
    imageUrl?: string | null;
  }[];
  licensePlate: LicensePlate;
  licensePlateTrailing?: ReactNode;
  /** Icons / badges shown after the plate (e.g. car-wheel identifiers). */
  headerTrailing?: ReactNode;
  carDisplayName?: string | null;
  totalLabel?: ReactNode;
  /** When true, only sales items (and optional total) are shown — use when vehicle info is shown elsewhere. */
  hideVehicleHeader?: boolean;
  footer?: ReactNode;
};

export const CarWithSalesItemsInfo = ({
  salesItems,
  licensePlate,
  licensePlateTrailing,
  headerTrailing,
  carDisplayName,
  totalLabel,
  hideVehicleHeader = false,
  footer
}: Props) => {
  const carDisplay = carDisplayName ?? '';
  return (
    <div className='flex flex-col gap-1.5'>
      {!hideVehicleHeader && (
        <div className='flex justify-between gap-2'>
          <div className='flex min-w-0 flex-col'>
            <div className='flex items-center gap-2'>
              <NoddiIcon name='Car' size={14} />
              <span className='text-sm font-semibold'>
                {licensePlate.personalisedNumber
                  ? `${licensePlate.personalisedNumber} (${formatLicensePlate(licensePlate.number)})`
                  : formatLicensePlate(licensePlate.number)}
                {licensePlateTrailing && <> ({licensePlateTrailing})</>}
              </span>
              {headerTrailing}
            </div>
            {carDisplay && <span className='truncate text-xs text-muted-foreground'>{carDisplay}</span>}
          </div>
          {salesItems.length > 1 && (
            <span className='text-xs text-nowrap'>
              {salesItems.length} {pluralize(salesItems.length, 'item')}
            </span>
          )}
        </div>
      )}

      <div className='minimal-scrollbar-hover -mr-3 overflow-y-auto pr-3'>
        <div className='flex flex-col gap-1'>
          {salesItems.map((item, idx) => (
            <div
              key={`salesItem-${item.id}-${idx}`}
              className='flex justify-between gap-4 border-b-muted-foreground pb-1 text-xs [&:not(:last-child)]:border-b'
            >
              <span className='flex items-center gap-1 truncate'>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt='' className='size-3 shrink-0 object-contain' />
                ) : (
                  item.icon && <NoddiIcon name={item.icon} size='xSmall' className='shrink-0' />
                )}
                {item.quantity && item.quantity > 1 ? `${item.quantity}x ` : ''}
                {item.name}
              </span>
              {item.priceLabel && <span className='shrink-0 text-nowrap text-muted-foreground'>{item.priceLabel}</span>}
            </div>
          ))}
        </div>
      </div>
      {totalLabel && (
        <div className='flex justify-between border-t pt-1.5 pb-1 text-xs font-semibold'>
          <span>Total</span>
          <span>{totalLabel}</span>
        </div>
      )}
      {footer}
    </div>
  );
};
